import type { RoomPreset, SurfaceKey } from '@/types/room';

/**
 * Ordered list of canned rooms shown in the preset grid on Step 1.
 *
 * The last entry (`custom`) is a sentinel — it carries no `dims`/`materials`
 * and flips the wizard to user-driven mode when activated.
 */
export const ROOM_PRESETS: readonly RoomPreset[] = [
  {
    id: 'vocal-booth',
    name: 'Vocal Booth',
    desc: 'Small, quiet space for voice recording',
    icon: 'mic',
    dims: { L: 2.5, W: 2.0, H: 2.4 },
    materials: {
      floor: 'Carpet (thick)',
      ceiling: 'Acoustic tile',
      'wall-n': 'Drywall',
      'wall-s': 'Drywall',
      'wall-e': 'Drywall',
      'wall-w': 'Drywall',
    },
  },
  {
    id: 'bedroom',
    name: 'Bedroom Studio',
    desc: 'Typical home production setup',
    icon: 'bed-single',
    dims: { L: 4.0, W: 3.5, H: 2.5 },
    materials: {
      floor: 'Wood',
      ceiling: 'Drywall',
      'wall-n': 'Drywall',
      'wall-s': 'Drywall',
      'wall-e': 'Drywall',
      'wall-w': 'Drywall',
    },
  },
  {
    id: 'control-room',
    name: 'Control Room',
    desc: 'Treated mixing room with panels',
    icon: 'sliders-horizontal',
    dims: { L: 5.0, W: 4.0, H: 2.6 },
    materials: {
      floor: 'Carpet + pad',
      ceiling: 'Acoustic tile',
      'wall-n': 'Wood panels',
      'wall-s': 'Drywall',
      'wall-e': 'Wood panels',
      'wall-w': 'Wood panels',
    },
  },
  {
    id: 'living-room',
    name: 'Living Room',
    desc: 'Spacious room with hard surfaces',
    icon: 'sofa',
    dims: { L: 6.0, W: 4.5, H: 2.8 },
    materials: {
      floor: 'Wood',
      ceiling: 'Drywall',
      'wall-n': 'Drywall',
      'wall-s': 'Drywall',
      'wall-e': 'Drywall',
      'wall-w': 'Drywall',
    },
  },
  {
    id: 'garage',
    name: 'Garage',
    desc: 'Large, echoey untreated space',
    icon: 'warehouse',
    dims: { L: 7.0, W: 5.0, H: 3.0 },
    materials: {
      floor: 'Concrete',
      ceiling: 'Concrete',
      'wall-n': 'Concrete',
      'wall-s': 'Concrete',
      'wall-e': 'Concrete',
      'wall-w': 'Concrete',
    },
  },
  {
    id: 'custom',
    name: 'Custom',
    desc: 'Set your own dimensions',
    icon: 'pencil',
  },
];

/** Preset selected on first paint. Matches the legacy default. */
export const DEFAULT_PRESET_ID = 'bedroom';

export const SURFACE_KEYS: readonly SurfaceKey[] = [
  'floor',
  'ceiling',
  'wall-n',
  'wall-s',
  'wall-e',
  'wall-w',
] as const;

/** Human-friendly label for a surface key (used in the material picker). */
export const SURFACE_LABELS: Record<SurfaceKey, string> = {
  floor: 'Floor',
  ceiling: 'Ceiling',
  'wall-n': 'Wall N',
  'wall-s': 'Wall S',
  'wall-e': 'Wall E',
  'wall-w': 'Wall W',
};

export function getPreset(id: string): RoomPreset | undefined {
  return ROOM_PRESETS.find((p) => p.id === id);
}

export function getDefaultPreset(): RoomPreset {
  const p = getPreset(DEFAULT_PRESET_ID);
  if (!p || !p.dims || !p.materials) {
    throw new Error(
      `Default preset "${DEFAULT_PRESET_ID}" must be non-custom and include dims + materials.`,
    );
  }
  return p;
}
